import { useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  Sparkle, Cpu, Brain, Books, MagnifyingGlass, ArrowClockwise, Eye,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { PromoPageHeader } from "../components/PromoPageHeader";
import { KpiCard } from "../components/KpiCard";
import { EmptyState } from "../components/EmptyState";
import { TableSkeleton } from "../components/Skeleton";
import { PromoSelect } from "../components/PromoSelect";
import { StatusBadge } from "../components/StatusBadge";
import { DataTable } from "../components/DataTable";
import { TemplateDetailPanel } from "../components/templates/TemplateDetailPanel";
import { useWizardData } from "../usePromo";
import { PLATFORM_BY_KEY } from "../platforms";
import { fmtDateTime } from "../campaignUtils";

const PLATFORM_KEYS = ["facebook", "instagram", "linkedin", "x", "pinterest"];

// Presentation-only derivation from the prompt key + template (no business logic).
function parseTemplate(p) {
  const parts = String(p.key).split(".");
  const platform = PLATFORM_KEYS.find((k) => parts.includes(k)) || null;
  let category = "preset";
  if (p.key.includes("ai.complete")) category = "ai_complete";
  else if (p.key.includes("ai.enhance")) category = "ai_enhance";
  else if (p.key.includes("layout.original")) category = "original";
  const name = parts.slice(1).map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(" · ");
  const variables = [...new Set((String(p.template || "").match(/\{(\w+)\}/g) || []).map((v) => v.slice(1, -1)))];
  return { ...p, platform, category, name, variables };
}

export default function AITemplates() {
  const { t } = useTranslation();
  const wd = useWizardData();

  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [platform, setPlatform] = useState("all");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(null);

  const catLabel = useCallback((c) => t(`promo.templates.cat.${c}`, c), [t]);

  const templates = useMemo(() => (wd.prompts || []).map(parseTemplate), [wd.prompts]);
  const categories = useMemo(() => [...new Set(templates.map((x) => x.category))], [templates]);
  const platforms = useMemo(() => [...new Set(templates.map((x) => x.platform).filter(Boolean))], [templates]);

  const filtered = useMemo(() => templates.filter((x) => {
    if (category !== "all" && x.category !== category) return false;
    if (platform !== "all" && x.platform !== platform) return false;
    if (status !== "all" && status !== "active") return false; // all registry templates are active
    if (q) {
      const hay = `${x.key} ${x.name} ${catLabel(x.category)}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [templates, category, platform, status, q, catLabel]);

  const opt = (vals, allLabel) => [{ value: "all", label: allLabel }, ...vals];

  const cols = [
    { key: "name", label: t("promo.templates.col.name"), render: (x) => {
      const p = x.platform ? PLATFORM_BY_KEY[x.platform] : null;
      return <span className="flex items-center gap-2 font-medium">{p?.Icon && <p.Icon size={13} />}{x.name}</span>;
    } },
    { key: "category", label: t("promo.templates.col.category"), render: (x) => <StatusBadge status="info" dot={false} label={catLabel(x.category)} /> },
    { key: "platform", label: t("promo.templates.col.platform"), render: (x) => x.platform ? (PLATFORM_BY_KEY[x.platform]?.label || x.platform) : "—" },
    { key: "key", label: t("promo.templates.col.key"), render: (x) => <span className="font-mono text-xs text-muted-foreground">{x.key}</span> },
    { key: "version", label: t("promo.templates.col.version"), align: "right", render: (x) => <span className="tabular-nums">v{x.versions}</span> },
    { key: "status", label: t("promo.templates.col.status"), render: () => <StatusBadge status="active" label={t("promo.templates.status.active")} /> },
    { key: "actions", label: "", align: "right", render: (x) => (
      <button onClick={(e) => { e.stopPropagation(); setSelected(x); }} className="rounded-md border border-border p-1.5 hover:bg-accent" data-testid={`template-view-${x.key}`}><Eye size={14} /></button>
    ) },
  ];

  return (
    <div className="space-y-8" data-testid="templates-page">
      <PromoPageHeader
        overline={t("promo.nav.templates")}
        title={t("promo.templates.title")}
        description={t("promo.templates.subtitle")}
        actions={
          <button onClick={() => window.location.reload()} className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground" data-testid="templates-refresh">
            <ArrowClockwise size={14} className={wd.loading ? "animate-spin" : ""} /> {t("promo.conn.refresh")}
          </button>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="templates-kpis">
        <KpiCard index={0} testId="tpl-kpi-total" label={t("promo.templates.kpi.total")} value={templates.length} Icon={Sparkle} />
        <KpiCard index={1} testId="tpl-kpi-active" label={t("promo.templates.kpi.active")} value={templates.length} Icon={Sparkle} hint={t("promo.templates.allActive")} />
        <KpiCard index={2} testId="tpl-kpi-providers" label={t("promo.templates.kpi.providers")} value={(wd.providers || []).length} Icon={Cpu} />
        <KpiCard index={3} testId="tpl-kpi-kbs" label={t("promo.templates.kpi.kbs")} value={(wd.kbs || []).length} Icon={Books} />
      </div>

      {/* Toolbar */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("promo.templates.search")} className="bg-card pl-9" data-testid="templates-search" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-40"><PromoSelect value={category} onChange={setCategory} testId="templates-filter-category"
            options={opt(categories.map((c) => ({ value: c, label: catLabel(c) })), t("promo.templates.allTypes"))} /></div>
          <div className="w-36"><PromoSelect value={platform} onChange={setPlatform} testId="templates-filter-platform"
            options={opt(platforms.map((p) => ({ value: p, label: PLATFORM_BY_KEY[p]?.label || p })), t("promo.templates.allPlatforms"))} /></div>
          <div className="w-36"><PromoSelect value={status} onChange={setStatus} testId="templates-filter-status"
            options={[{ value: "all", label: t("promo.templates.allStatus") }, { value: "active", label: t("promo.templates.status.active") }]} /></div>
        </div>
      </div>

      {/* Section 1 — Templates List */}
      {wd.loading ? (
        <TableSkeleton rows={6} cols={6} />
      ) : templates.length === 0 ? (
        <EmptyState Icon={Sparkle} title={t("promo.templates.emptyTitle")} description={t("promo.templates.emptyDesc")} />
      ) : filtered.length === 0 ? (
        <EmptyState Icon={MagnifyingGlass} title={t("promo.catalog.noMatch")} description={t("promo.catalog.noMatchDesc")} />
      ) : (
        <DataTable columns={cols} rows={filtered} rowKey="key" onRowClick={(x) => setSelected(x)} testId="templates-table" />
      )}

      {/* Section 3 & 4 — Provider + Knowledge Base overview */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <section className="space-y-4" data-testid="templates-providers">
          <h3 className="text-lg font-medium tracking-tight">{t("promo.templates.sec.providers")}</h3>
          {(wd.providers || []).length === 0 ? (
            <EmptyState Icon={Cpu} title={t("promo.templates.noProviders")} />
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {wd.providers.map((p, i) => (
                <motion.div key={p.id || p.provider} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: i * 0.04 }}
                  className="rounded-lg border border-border bg-card p-4" data-testid={`provider-card-${p.provider}`}>
                  <div className="flex items-center gap-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-accent/40"><Brain size={15} /></span>
                    <div className="min-w-0">
                      <div className="text-sm font-medium capitalize">{p.provider}</div>
                      {p.model && <div className="truncate font-mono text-xs text-muted-foreground">{p.model}</div>}
                    </div>
                  </div>
                  {Array.isArray(p.capabilities) && p.capabilities.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {p.capabilities.map((c) => (
                        <span key={c} className="rounded-full border border-border bg-accent/30 px-2 py-0.5 text-[10px] font-medium text-muted-foreground">{c}</span>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </section>

        <section className="space-y-4" data-testid="templates-kbs">
          <h3 className="text-lg font-medium tracking-tight">{t("promo.templates.sec.kbs")}</h3>
          {(wd.kbs || []).length === 0 ? (
            <EmptyState Icon={Books} title={t("promo.templates.noKbs")} />
          ) : (
            <DataTable
              testId="templates-kb-table"
              rowKey="id"
              rows={wd.kbs}
              columns={[
                { key: "name", label: t("promo.templates.kb.name"), render: (k) => <span className="font-medium">{k.name}</span> },
                { key: "desc", label: t("promo.templates.kb.description"), render: (k) => k.description ? <span className="block max-w-[220px] truncate text-muted-foreground">{k.description}</span> : "—" },
                { key: "items", label: t("promo.templates.kb.items"), align: "right", render: (k) => k.item_count != null ? <span className="tabular-nums">{k.item_count}</span> : "—" },
                { key: "created", label: t("promo.templates.kb.created"), render: (k) => k.created_at ? fmtDateTime(k.created_at) : "—" },
              ]}
            />
          )}
        </section>
      </div>

      <TemplateDetailPanel template={selected} onClose={() => setSelected(null)} catLabel={catLabel} />
    </div>
  );
}
